import type { EditorState } from '@tiptap/pm/state';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import {
  TIPTAP_ACTION_BUTTON_TYPE,
  TIPTAP_AUDIO_TYPE,
  TIPTAP_FIGURE_TYPE,
  TIPTAP_FILE_TYPE,
  TIPTAP_GALLERY_TYPE,
  TIPTAP_GIF_TYPE,
  TIPTAP_HTML_TYPE,
  TIPTAP_LINK_BUTTON_TYPE,
  TIPTAP_LINK_PREVIEW_TYPE,
  TIPTAP_VIDEO_TYPE,
} from './../../ricos-content/consts';
import { Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';

export const createHandleTextWrapPlugin = () => {
  return new Plugin({
    key: new PluginKey('handle-text-wrap'),
    appendTransaction: (_, oldState: EditorState, newState: EditorState) => {
      if (newState.doc === oldState.doc) {
        return;
      }
      const tr = newState.tr;

      // Disable text wrapping for all wrappable items added inside not allowed nodes
      tr.doc.descendants((node, pos) => {
        if (allowsTextWrap(node.type.name)) {
          return;
        }

        node.forEach((child, childPos) => {
          if (!isTextWrappable(child.type.name)) {
            return false;
          }

          const [tempNode, additionalPos] = isFigure(child.type.name)
            ? [child.firstChild, 1]
            : [child, 0];

          tr.setNodeAttribute(childPos + pos + additionalPos + 1, 'containerData', {
            ...(tempNode?.attrs.containerData ?? {}),
            textWrap: false,
          });
        });
      });

      // Remove text wrap from wrappable items which are not inside allowed nodes in two steps:
      // 1. Collect all wrappable items which were inside not allowed nodes
      const wrappableNodeIdsWithinNotAllowedNodes: string[] = [];
      oldState.doc.descendants((node, _pos, parent) => {
        if (isTextWrappable(node.type.name) && !allowsTextWrap(parent?.type.name)) {
          wrappableNodeIdsWithinNotAllowedNodes.push(node.attrs.id);
        }
      });

      // 2. Check if wrappable items are still in not allowed nodes, if not, remove text wrap
      tr.doc.descendants((node, pos, parent) => {
        if (
          wrappableNodeIdsWithinNotAllowedNodes.includes(node.attrs.id) &&
          allowsTextWrap(parent?.type.name)
        ) {
          const [tempNode, additionalPos] = isFigure(node.type.name)
            ? [node.firstChild, 1]
            : [node, 0];

          tr.setNodeAttribute(pos + additionalPos, 'containerData', {
            ...(tempNode?.attrs.containerData ?? {}),
            textWrap: true,
          });
        }
      });

      return tr;
    },
  });
};

const isTextWrappable = (nodeType: string) => {
  return [
    TIPTAP_FIGURE_TYPE,
    TIPTAP_GALLERY_TYPE,
    TIPTAP_GIF_TYPE,
    TIPTAP_VIDEO_TYPE,
    TIPTAP_FILE_TYPE,
    TIPTAP_HTML_TYPE,
    TIPTAP_LINK_PREVIEW_TYPE,
    TIPTAP_LINK_BUTTON_TYPE,
    TIPTAP_ACTION_BUTTON_TYPE,
    TIPTAP_AUDIO_TYPE,
  ].includes(nodeType as Node_Type);
};

const isFigure = (nodeType: string) => {
  return [TIPTAP_FIGURE_TYPE as string].includes(nodeType);
};

const allowsTextWrap = (nodeType?: string) => {
  return ![Node_Type.LIST_ITEM, Node_Type.LAYOUT_CELL].includes(nodeType as Node_Type);
};
